/**
 * BRAND THEMES — the single source of truth for how each platform LOOKS.
 *
 * LitLaunch defines the structure of every page and component. AfterTide
 * reuses that structure exactly and differs only through the tokens below:
 * palette, fonts, and tag shape. Components read these tokens via
 * `useBrand()` (or the `useDarkClasses` / `useRoleTheme` hooks, which are
 * built on top of them) instead of branching on the platform themselves.
 *
 * Rule of thumb: if a component needs `isAfterTide ? 'x' : 'y'` for a colour,
 * the value belongs here as a token.
 *
 * All class strings are literal so Tailwind's content scanner generates them.
 */

export type BrandId = 'litlaunch' | 'aftertide'

/** Surface tokens consumed through `useDarkClasses()`. */
export interface SurfaceTokens {
  page: string
  card: string
  cardSubtle: string
  cardElevated: string
  surface: string
  surfaceMuted: string
  border: string
  borderSubtle: string
  borderAccent: string
  borderAccentStrong: string
  divider: string
  textPrimary: string
  textSecondary: string
  textMuted: string
  textFaint: string
  textHeading: string
  input: string
  inputOnCard: string
  nav: string
  dropdown: string
  dropdownItemHover: string
  badgeNeutral: string
  /** Floating carousel arrow buttons */
  carouselArrow: string
  /** Translucent sticky bar (mobile top bar) */
  navTranslucent: string
  /** Icon / secondary control colour in navigation chrome */
  chromeIcon: string
  isDark: boolean
}

/** Accent tokens consumed through `useRoleTheme()`. */
export interface RoleTokens {
  primaryButton: string
  secondaryButton: string
  outlineButton: string
  inputFocus: string
  checkbox: string
  toggle: string
  activeTab: string
  inactiveTab: string
  link: string
  badge: string
  borderActive: string
  borderFocus: string
  textPrimary: string
  textHover: string
  bgLight: string
  bgMedium: string
  bgDark: string
  accentLink: string
  voteButton: string
  badgeBg: string
  accentSecondaryText: string
  accentSecondaryBg: string
  accentSecondaryHover: string
  accentSecondaryTint: string
  accentSecondaryTintHover: string
  accentSecondaryBorder: string
  secondaryActionButton: string
}

/** Brand-level tokens that don't vary with dark mode or user role. */
export interface BrandTheme {
  id: BrandId
  /** Extra font class applied to the app shell */
  fontClass: string
  /** Tag / badge corner shape */
  tagRadius: 'rounded-full' | 'rounded-sm'
  /**
   * AfterTide outlines its coloured tags with a faint border of the tag's own
   * hue. LitLaunch tags are borderless. Components call `tagOutline(color)`.
   */
  outlineTags: boolean
  /**
   * When set, the brand ignores the dark-mode toggle and always renders these
   * surfaces. LitLaunch leaves it null and derives surfaces from dark mode.
   */
  fixedSurfaces: SurfaceTokens | null
  /** When set, replaces role-based accents entirely. */
  fixedRoleTokens: RoleTokens | null

  /** App shell background + text (LitLaunch's shell is mode-dependent). */
  shell: string | null

  accent: {
    /** Inline icon / emphasis colour on page surfaces */
    icon: string
    /** Secondary inline icon colour (e.g. Author Spotlight sparkle) */
    iconWarm: string
    /** Large headline accent (home tagline) */
    headline: string
    /** Solid accent fill for small labels / badges */
    solidBadge: string
    /** Soft tinted tile behind an accent icon */
    iconTile: string
    /** Focus ring for inputs */
    focusRing: string
    /** Verified checkmark colour */
    verified: string
    /** Solid brand button (role-independent) */
    button: string
    /** Inline text link colour */
    link: string
    /** Page title in the mobile top bar (null = follow surface heading) */
    chromeTitle: string | null
  }

  /**
   * Accents that LitLaunch varies by user role (reader = indigo, vendor =
   * purple). AfterTide uses gold for both.
   */
  roleAccent: Record<
    'reader' | 'vendor',
    {
      /** Active bottom-nav item */
      navActive: string
      /** Count bubble on header / top-bar icons */
      countBadge: string
      /** Highlighted icon in the mobile top bar */
      icon: string
      /** Desktop nav interaction states */
      hoverText: string
      focusText: string
      hoverBorder: string
      focusBorder: string
      activeBorder: string
      focusVisibleRing: string
    }
  >

  /** Story Stack discovery accent (LitLaunch uses periwinkle #7E8CE0). */
  hero: {
    text: string
    bg: string
    bgHover: string
    border: string
    focusBorder: string
    hoverBorder: string
    hoverText: string
    onBg: string
  }

  /** Secondary brand accent used for vendor-ish / "virtual" highlights. */
  secondary: {
    text: string
    bg: string
    fab: string
    fabActive: string
    onFab: string
  }

  /** Dark nav bar strip behind header / bottom bar contents */
  chromeBar: string

  /** Hover overlay shown over book covers */
  coverOverlay: {
    scrim: string
    linkHover: string
    linkHoverSoft: string
    meta: string
    /** Fixed tag style inside the overlay (null = per-tag colour) */
    tag: string | null
    /** Cover frame hover treatment */
    frameHover: string
  }

  /** Date block on event cards */
  eventDateBlock: string
  /** Badge for virtual events */
  eventVirtual: string
  /** Event card call-to-action button */
  eventCta: string

  /** Placeholder tile behind book-box art */
  promoTile: string

  footer: {
    root: string
    tagline: string
    link: string
    socialLink: string
    heading: string
    divider: string
    dividerSoft: string
    copyright: string
    wordmark: string
    showWordmarkIcon: boolean
    sisterLink: string
    sisterGlyph: string
  }

  newsletter: {
    stripe: string
    iconTile: string
  }

  vendorCta: {
    panel: string
    button: string
    check: string
  }

  /** Brand toggle pill (header) */
  togglePill: string
}

// ─── AfterTide fixed palette ─────────────────────────────────────────────────
// Navy #1a1a2e / #16213e, gold #e2b04a, pink #c77dba, cream #f5f0e8,
// muted #a0a0b0.

const aftertideSurfaces: SurfaceTokens = {
  page: 'bg-[#1a1a2e]',
  card: 'bg-[#16213e] border-[#16213e]',
  cardSubtle: 'bg-[#16213e] border-[#e2b04a]/10',
  cardElevated: 'bg-[#1a1a2e] border-[#e2b04a]/10',
  surface: 'bg-[#16213e]',
  surfaceMuted: 'bg-[#16213e]/50',
  border: 'border-[#16213e]',
  borderSubtle: 'border-[#e2b04a]/10',
  borderAccent: 'border-[#e2b04a]/20',
  borderAccentStrong: 'border-[#e2b04a]/30',
  divider: 'border-[#16213e]',
  textPrimary: 'text-[#f5f0e8]',
  textSecondary: 'text-[#a0a0b0]',
  textMuted: 'text-[#a0a0b0]',
  textFaint: 'text-[#a0a0b0]/60',
  textHeading: 'text-[#f5f0e8]',
  input:
    'bg-[#16213e] border-[#e2b04a]/30 text-[#f5f0e8] placeholder-[#a0a0b0]',
  inputOnCard:
    'bg-[#1a1a2e] border-[#e2b04a]/20 text-[#f5f0e8] placeholder-[#a0a0b0]',
  nav: 'bg-[#1a1a2e] border-[#16213e]',
  dropdown: 'bg-[#1a1a2e] border-[#16213e]',
  dropdownItemHover: 'hover:bg-[#16213e]',
  badgeNeutral: 'bg-[#16213e] text-[#a0a0b0]',
  carouselArrow: 'bg-[#1a1a2e]/90 text-[#f5f0e8] border-[#16213e]',
  navTranslucent: 'bg-[#1a1a2e]/95 border-b border-[#16213e]',
  chromeIcon: 'text-[#a0a0b0]',
  isDark: true,
}

const aftertideRoleTokens: RoleTokens = {
  primaryButton:
    'bg-[#e2b04a] hover:bg-[#d1a03a] text-[#1a1a2e] focus:ring-[#e2b04a]',
  secondaryButton: 'bg-[#e2b04a]/10 text-[#e2b04a] hover:bg-[#e2b04a]/20',
  outlineButton: 'border-[#e2b04a]/30 text-[#e2b04a] hover:bg-[#e2b04a]/10',
  inputFocus: 'focus:border-[#e2b04a] focus:ring-[#e2b04a]',
  checkbox: 'text-[#e2b04a] focus:ring-[#e2b04a]',
  toggle: 'peer-checked:bg-[#e2b04a] peer-focus:ring-[#e2b04a]',
  activeTab: 'bg-[#e2b04a]/10 text-[#e2b04a]',
  inactiveTab: 'text-[#a0a0b0] hover:bg-[#16213e]',
  link: 'text-[#e2b04a] hover:text-[#f5f0e8]',
  badge: 'bg-[#e2b04a]/10 text-[#e2b04a]',
  borderActive: 'border-[#e2b04a]',
  borderFocus: 'focus:border-[#e2b04a]',
  textPrimary: 'text-[#e2b04a]',
  textHover: 'hover:text-[#f5f0e8]',
  bgLight: 'bg-[#e2b04a]/5',
  bgMedium: 'bg-[#e2b04a]/10',
  bgDark: 'bg-[#e2b04a]',
  accentLink: 'text-[#e2b04a] hover:text-[#f5f0e8]',
  voteButton: 'bg-[#e2b04a]/10 text-[#e2b04a] hover:bg-[#e2b04a]/20',
  badgeBg: 'bg-[#e2b04a]',
  accentSecondaryText: 'text-[#c77dba]',
  accentSecondaryBg: 'bg-[#c77dba]',
  accentSecondaryHover: 'hover:bg-[#b56ca8]',
  accentSecondaryTint: 'bg-[#c77dba]/10',
  accentSecondaryTintHover: 'bg-[#c77dba]/20',
  accentSecondaryBorder: 'border-[#c77dba]/30',
  secondaryActionButton:
    'bg-[#c77dba] hover:bg-[#b56ca8] text-[#1a1a2e] font-medium',
}

// AfterTide uses gold for both roles
const aftertideRoleAccent = {
  navActive: 'text-[#e2b04a]',
  countBadge: 'bg-[#e2b04a] text-[#1a1a2e]',
  icon: 'text-[#e2b04a]',
  hoverText: 'hover:text-[#e2b04a]',
  focusText: 'focus:text-[#e2b04a]',
  hoverBorder: 'hover:border-[#e2b04a]',
  focusBorder: 'focus:border-[#e2b04a]',
  activeBorder: 'border-[#e2b04a]',
  focusVisibleRing: 'focus-visible:ring-[#e2b04a]',
}

// ─── Themes ──────────────────────────────────────────────────────────────────

export const litlaunchTheme: BrandTheme = {
  id: 'litlaunch',
  fontClass: '',
  tagRadius: 'rounded-full',
  outlineTags: false,
  fixedSurfaces: null,
  fixedRoleTokens: null,
  shell: null,
  accent: {
    icon: 'text-indigo-500',
    iconWarm: 'text-amber-500',
    headline: 'text-[#7E8CE0]',
    solidBadge: 'bg-indigo-500 text-white',
    iconTile: 'bg-indigo-50',
    focusRing: 'focus:ring-indigo-500',
    verified: 'text-blue-500',
    button: 'bg-indigo-600 text-white hover:bg-indigo-700',
    link: 'text-indigo-600',
    chromeTitle: null,
  },
  roleAccent: {
    reader: {
      navActive: 'text-indigo-600',
      countBadge: 'bg-indigo-600 text-white',
      icon: 'text-indigo-500',
      hoverText: 'hover:text-indigo-500',
      focusText: 'focus:text-indigo-500',
      hoverBorder: 'hover:border-indigo-500',
      focusBorder: 'focus:border-indigo-500',
      activeBorder: 'border-indigo-600',
      focusVisibleRing: 'focus-visible:ring-indigo-500',
    },
    vendor: {
      navActive: 'text-purple-700',
      countBadge: 'bg-purple-600 text-white',
      icon: 'text-purple-500',
      hoverText: 'hover:text-purple-600',
      focusText: 'focus:text-purple-600',
      hoverBorder: 'hover:border-purple-600',
      focusBorder: 'focus:border-purple-600',
      activeBorder: 'border-purple-700',
      focusVisibleRing: 'focus-visible:ring-purple-500',
    },
  },
  hero: {
    text: 'text-[#7E8CE0]',
    bg: 'bg-[#7E8CE0]',
    bgHover: 'hover:bg-[#6A78D1]',
    border: 'border-[#7E8CE0]',
    focusBorder: 'focus:border-[#7E8CE0]',
    hoverBorder: 'hover:border-[#7E8CE0]',
    hoverText: 'hover:text-[#7E8CE0]',
    onBg: 'text-white',
  },
  secondary: {
    text: 'text-purple-600',
    bg: 'bg-indigo-500',
    fab: 'bg-purple-600 shadow-purple-500/25',
    fabActive: 'bg-purple-600 shadow-purple-500/30',
    onFab: 'text-white',
  },
  chromeBar: 'bg-[#111827]',
  coverOverlay: {
    scrim: 'bg-black/85',
    linkHover: 'hover:text-indigo-300',
    linkHoverSoft: 'hover:text-indigo-200',
    meta: 'text-indigo-300',
    tag: null,
    frameHover: 'group-hover/card:scale-105',
  },
  eventDateBlock: 'bg-indigo-900/80',
  eventVirtual: 'bg-indigo-500',
  eventCta:
    'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:hover:bg-indigo-900/50',
  promoTile: 'bg-gradient-to-br from-indigo-500 to-violet-600',
  footer: {
    root: 'bg-gray-900 text-white',
    tagline: 'text-gray-300',
    link: 'text-gray-300 hover:text-white',
    socialLink: 'text-gray-400 hover:text-white',
    heading: '',
    divider: 'border-gray-800',
    dividerSoft: 'border-gray-800/50',
    copyright: 'text-gray-400',
    wordmark: 'text-xl font-bold',
    showWordmarkIcon: true,
    sisterLink: 'text-gray-500 hover:text-gray-300',
    sisterGlyph: 'text-gray-400 group-hover:text-purple-400',
  },
  newsletter: {
    stripe: 'bg-gradient-to-r from-indigo-500 to-purple-500',
    iconTile: 'bg-indigo-50',
  },
  vendorCta: {
    panel: 'bg-gradient-to-r from-indigo-500 to-indigo-700',
    button: 'bg-white text-indigo-700 hover:bg-indigo-50',
    check: 'bg-white/20 text-white',
  },
  togglePill: 'font-bold',
}

export const aftertideTheme: BrandTheme = {
  id: 'aftertide',
  fontClass: 'aftertide-fonts',
  tagRadius: 'rounded-sm',
  outlineTags: true,
  fixedSurfaces: aftertideSurfaces,
  fixedRoleTokens: aftertideRoleTokens,
  shell: 'bg-[#1a1a2e] text-[#f5f0e8]',
  accent: {
    icon: 'text-[#e2b04a]',
    iconWarm: 'text-[#e2b04a]',
    headline: 'text-[#E2B04A]',
    solidBadge: 'bg-[#e2b04a] text-[#1a1a2e]',
    iconTile: 'bg-[#e2b04a]/10',
    focusRing: 'focus:ring-[#e2b04a]',
    verified: 'text-[#e2b04a]',
    button: 'bg-[#e2b04a] text-[#1a1a2e] hover:bg-[#d1a03a]',
    link: 'text-[#e2b04a]',
    chromeTitle: 'text-[#e2b04a]',
  },
  roleAccent: {
    reader: aftertideRoleAccent,
    vendor: aftertideRoleAccent,
  },
  hero: {
    text: 'text-[#e2b04a]',
    bg: 'bg-[#e2b04a]',
    bgHover: 'hover:bg-[#d1a03a]',
    border: 'border-[#e2b04a]',
    focusBorder: 'focus:border-[#e2b04a]',
    hoverBorder: 'hover:border-[#e2b04a]',
    hoverText: 'hover:text-[#e2b04a]',
    onBg: 'text-[#1a1a2e]',
  },
  secondary: {
    text: 'text-[#c77dba]',
    bg: 'bg-[#c77dba]',
    fab: 'bg-[#c77dba] shadow-[#c77dba]/20',
    fabActive: 'bg-[#c77dba] shadow-[#c77dba]/30',
    onFab: 'text-[#1a1a2e]',
  },
  chromeBar: 'bg-[#1a1a2e]',
  coverOverlay: {
    scrim: 'bg-[#1a1a2e]/90',
    linkHover: 'hover:text-[#e2b04a]',
    linkHoverSoft: 'hover:text-[#e2b04a]',
    meta: 'text-[#e2b04a]/80',
    tag: 'bg-[#1a1a2e] border border-[#e2b04a]/30 text-[#e2b04a]',
    frameHover:
      'border border-transparent group-hover/card:border-[#e2b04a]/50 group-hover/card:shadow-[0_0_15px_rgba(226,176,74,0.15)]',
  },
  eventDateBlock: 'bg-[#1a1a2e]/80',
  eventVirtual: 'bg-[#c77dba]',
  eventCta: 'bg-[#e2b04a] text-[#1a1a2e] hover:bg-[#d1a03a]',
  promoTile: 'bg-gradient-to-br from-[#2d1b3d] to-[#1a1a2e]',
  footer: {
    root: 'bg-[#1a1a2e] text-[#f5f0e8]',
    tagline: 'text-[#a0a0b0]',
    link: 'text-[#a0a0b0] hover:text-[#e2b04a]',
    socialLink: 'text-[#a0a0b0] hover:text-[#e2b04a]',
    heading: 'text-[#f5f0e8]',
    divider: 'border-[#16213e]',
    dividerSoft: 'border-[#16213e]/50',
    copyright: 'text-[#a0a0b0]',
    wordmark: 'font-playfair text-xl font-bold text-[#e2b04a]',
    showWordmarkIcon: false,
    sisterLink: 'text-[#a0a0b0] hover:text-[#e2b04a]',
    sisterGlyph: 'text-[#a0a0b0] group-hover:text-[#e2b04a]',
  },
  newsletter: {
    stripe: 'bg-gradient-to-r from-[#e2b04a] to-[#c77dba]',
    iconTile: 'bg-[#e2b04a]/10',
  },
  vendorCta: {
    panel: 'bg-gradient-to-r from-[#2d1b3d] to-[#1a1a2e]',
    button: 'bg-[#e2b04a] text-[#1a1a2e] hover:bg-[#d1a03a]',
    check: 'bg-[#e2b04a]/20 text-[#e2b04a]',
  },
  togglePill: 'font-playfair font-bold tracking-wide',
}

export const brandThemes: Record<BrandId, BrandTheme> = {
  litlaunch: litlaunchTheme,
  aftertide: aftertideTheme,
}
